import React, { useEffect } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

import type { IResourceDatabaseField } from '@/api/types';
import type { IDatabaseFieldColumnNames } from '@/components/common';

import { databaseFieldSortingFunctions } from '@/helpers';
import { useAppSelector, useTableSort } from '@/hooks';
import { Mobile, TabletAndDesktop } from '@/breakpoints-config/breakpoints';
import { selectDatabaseFieldTypeDict, selectPendingAction } from '@/store/selectors/resourcesSelectors';
import { ResourceDatabaseFieldsTable } from './ResourceDatabaseFieldsTable';
import { ResourceDatabaseFieldsTableMobile } from './ResourceDatabaseFieldsTableMobile';
import { getDatabaseFieldsList, getDatabaseFieldTypes } from '@/store/slices/resourcesSlice';
import { LoaderWrapper, TabsResourcesNoData, ResourceDatabaseTableSkeleton } from '@/components/common';

const resourceDatabaseFieldsTableSelector = createSelector(
  selectDatabaseFieldTypeDict,
  selectPendingAction(getDatabaseFieldsList.typePrefix),
  selectPendingAction(getDatabaseFieldTypes.typePrefix),
  (fieldDict, isFieldsListPending, isFieldTypesPending) => ({
    fieldDict,
    isPending: isFieldsListPending || isFieldTypesPending,
  }),
);

interface IProps {
  items: IResourceDatabaseField[];
  className?: string;
}

const ResourceDatabaseFieldsContainer = ({ items, className }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { fieldDict, isPending } = useAppSelector(resourceDatabaseFieldsTableSelector);
  const { sortedData, onSort, setRawData, sortingState } = useTableSort<
    IDatabaseFieldColumnNames,
    IResourceDatabaseField
  >({
    initial: { name: 'DESC' },
    sortingFunctions: databaseFieldSortingFunctions,
  });

  useEffect(() => {
    const itemsWithType = items.map(item => ({
      ...item,
      type: fieldDict.find(type => type.id === item.rtxtype_Id)?.name,
    }));
    setRawData(itemsWithType);
  }, [fieldDict, items, setRawData]);

  return (
    <LoaderWrapper isLoading={isPending} loader={<ResourceDatabaseTableSkeleton />}>
      {sortedData.length ? (
        <>
          <TabletAndDesktop>
            <ResourceDatabaseFieldsTable
              items={sortedData}
              sortingState={sortingState}
              onSort={onSort}
              className={className}
            />
          </TabletAndDesktop>
          <Mobile>
            <ResourceDatabaseFieldsTableMobile items={sortedData} className={className} />
          </Mobile>
        </>
      ) : (
        <TabsResourcesNoData label={t`resources.databases.noFields`} />
      )}
    </LoaderWrapper>
  );
};

export { ResourceDatabaseFieldsContainer };
